import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ChefHat, Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function LoginPage() {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
    });
    
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isLogin) {
                await login(formData.email, formData.password);
                toast.success('Connexion réussie');
            } else {
                await register(formData.name, formData.email, formData.password);
                toast.success('Compte créé avec succès');
            }
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.detail || 'Une erreur est survenue';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-bg flex items-center justify-center p-4 sm:p-6">
            <div 
                className="absolute inset-0 opacity-20"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1727951415852-3a03a7127ff9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwY2F0ZXJpbmclMjBmb29kJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcxNTE1Mzg0fDA&ixlib=rb-4.1.0&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            />
            
            <div className="login-card w-full max-w-md p-5 sm:p-8 relative z-10 animate-fadeInUp">
                <div className="text-center mb-6 sm:mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#d4af37]/10 mb-3 sm:mb-4">
                        <ChefHat className="w-6 h-6 sm:w-8 sm:h-8 text-[#d4af37]" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#f8fafc]">Traiteur Hub</h1>
                    <p className="text-[#94a3b8] text-sm sm:text-base mt-1 sm:mt-2">
                        {isLogin ? 'Connectez-vous à votre espace' : 'Créez votre compte'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    {!isLogin && (
                        <div className="space-y-1.5 sm:space-y-2">
                            <Label htmlFor="name" className="text-[#94a3b8] text-sm">Nom</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[#475569]" />
                                <Input
                                    id="name"
                                    data-testid="register-name-input"
                                    type="text"
                                    placeholder="Votre nom"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="pl-9 sm:pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] placeholder:text-[#475569] focus:border-[#d4af37] text-sm sm:text-base h-10 sm:h-11"
                                    required={!isLogin}
                                />
                            </div>
                        </div>
                    )}

                    <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="email" className="text-[#94a3b8] text-sm">Email</Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[#475569]" />
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="pl-9 sm:pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] placeholder:text-[#475569] focus:border-[#d4af37] text-sm sm:text-base h-10 sm:h-11"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="password" className="text-[#94a3b8] text-sm">Mot de passe</Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[#475569]" />
                            <Input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                placeholder="••••••••"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="pl-9 sm:pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] placeholder:text-[#475569] focus:border-[#d4af37] text-sm sm:text-base h-10 sm:h-11"
                                required
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        data-testid="login-submit-btn"
                        disabled={loading}
                        className="w-full bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold h-10 sm:h-12 text-sm sm:text-base"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-[#020617]/30 border-t-[#020617] rounded-full animate-spin" />
                                Chargement...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                {isLogin ? 'Se connecter' : 'Créer mon compte'}
                                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                            </span>
                        )}
                    </Button>
                </form>

                <div className="mt-4 sm:mt-6 text-center">
                    <button
                        type="button"
                        data-testid="toggle-auth-mode-btn"
                        onClick={() => setIsLogin(!isLogin)}
                        className="text-[#94a3b8] hover:text-[#d4af37] transition-colors text-sm sm:text-base"
                    >
                        {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
                    </button>
                </div>
            </div>
        </div>
    );
}
